import React, { useState } from 'react';
import { Box, Button, TextField, Tooltip, Typography } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

interface PaginationControlsProps {
  data: any[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage?: number;
  alwaysShow?: boolean;
}

export default function PaginationControls({
  data,
  currentPage,
  setCurrentPage,
  itemsPerPage = 20,
  alwaysShow = false,
}: PaginationControlsProps) {
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const [currentPageInput, setCurrentPageInput] = useState(currentPage.toString());

  // Update input when currentPage changes externally
  React.useEffect(() => {
    setCurrentPageInput(currentPage.toString());
  }, [currentPage]);

  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 1.5,
          mb: 2,
          padding: { xs: '8px', sm: '12px' },
          borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          width: '100%',
          border: '1px solid rgba(241, 198, 43, 0.2)',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No results found
        </Typography>
      </Box>
    );
  }

  if (!alwaysShow && totalPages <= 1) {
    return null;
  }

  const handleCurrentPageChange = (value: string) => {
    setCurrentPageInput(value);
    const page = parseInt(value);
    if (page && page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mt: 1.5,
        mb: 2,
        gap: { xs: 0.25, sm: 0.25 },
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
        padding: { xs: '2px', sm: '4px' },
        borderRadius: '4px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        width: '100%',
        border: '1px solid rgba(241, 198, 43, 0.2)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Tooltip title="First Page">
        <Box
          sx={{
            width: { xs: '24px', sm: '32px' },
            visibility: currentPage > 2 ? 'visible' : 'hidden',
            display: { xs: 'none', sm: 'block' },
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Button
            color="warning"
            size="small"
            onClick={() => setCurrentPage(1)}
            fullWidth
            sx={{
              minHeight: { xs: '24px', sm: '28px' },
              padding: 0,
              position: 'relative',
              '&:hover': {
                zIndex: 3,
              },
            }}
          >
            <FirstPageIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
          </Button>
        </Box>
      </Tooltip>
      <Tooltip title="Previous Page">
        <Button
          color="warning"
          size="small"
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          sx={{
            width: { xs: '24px', sm: '32px' },
            minWidth: { xs: '24px', sm: '32px' },
            minHeight: { xs: '24px', sm: '28px' },
            padding: 0,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <NavigateBeforeIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
        </Button>
      </Tooltip>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          fontWeight: 'medium',
          minWidth: { xs: '60px', sm: '80px' },
          justifyContent: 'center',
          fontSize: { xs: '0.7rem', sm: '0.8rem' },
          height: { xs: '24px', sm: '28px' },
          background: 'none',
          border: 'none',
          boxShadow: 'none',
          padding: 0,
        }}
      >
        <TextField
          size="small"
          variant="standard"
          value={currentPageInput}
          onChange={(e) => handleCurrentPageChange(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleCurrentPageChange(currentPageInput)}
          inputProps={{
            style: {
              textAlign: 'center',
              padding: 0,
              height: '24px',
              fontSize: '0.9rem',
              minWidth: '28px',
              maxWidth: '40px',
            },
          }}
          sx={{
            mx: 0.5,
            '& .MuiInputBase-root': {
              height: { xs: '24px', sm: '28px' },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              background: 'none',
              boxShadow: 'none',
            },
            '& .MuiInput-underline:before': {
              borderBottom: '1px solid',
              opacity: 0.5,
            },
            '& .MuiInput-underline:hover:before': {
              borderBottom: (theme) => `2px solid ${theme.palette.warning.main}`,
              opacity: 1,
            },
            '& .MuiInput-underline:after': {
              borderBottom: (theme) => `2px solid ${theme.palette.warning.main}`,
            },
          }}
        />
        <span style={{ marginLeft: 4, verticalAlign: 'middle' }}>/ {Math.max(1, totalPages)}</span>
      </Box>
      <Tooltip title="Next Page">
        <Button
          color="warning"
          size="small"
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          sx={{
            width: { xs: '24px', sm: '32px' },
            minWidth: { xs: '24px', sm: '32px' },
            minHeight: { xs: '24px', sm: '28px' },
            padding: 0,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <NavigateNextIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
        </Button>
      </Tooltip>
      <Tooltip title="Last Page">
        <Box
          sx={{
            width: { xs: '24px', sm: '32px' },
            visibility: currentPage < totalPages - 1 ? 'visible' : 'hidden',
            display: { xs: 'none', sm: 'block' },
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Button
            color="warning"
            size="small"
            onClick={() => setCurrentPage(totalPages)}
            fullWidth
            sx={{
              minHeight: { xs: '24px', sm: '28px' },
              padding: 0,
              position: 'relative',
              '&:hover': {
                zIndex: 3,
              },
            }}
          >
            <LastPageIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
          </Button>
        </Box>
      </Tooltip>
    </Box>
  );
}
